/**
 * Created by rathompson on 5/10/22
 * File: AppsRenderer
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useContext, useMemo } from 'react'
import { launchAppInstance } from '../../../../../common/utils/Sparky'
import AppContext from '../../../../../common/context/AppContext'
import { Body, noop, Tooltip } from '@panwds/react-ui'
import { ARRAY } from '../../../../../common/utils/Utils'
import { CircleCheckIcon, ExclamationCircleIcon, ProgressCircleIcon, OpenNewBrowserIcon } from '@panwds/icons'
import { DEVICE_STATUSES } from '../../../../constants/Constants'
import { LABEL_NONE, TOOLTIP_ASSOCIATING, TOOLTIP_DISSOCIATING, TOOLTIP_FAILED, VIEW_APP_TOOLTIP } from '../../../../constants/LabelConstants'
import GlobalContext from '../../../../../common/context/GlobalContext'

const AppsRenderer = ({ row, value }) => {

  const { getAppName } = useContext(AppContext)
  const { instanceReducer } = useContext(GlobalContext)
  const { instanceMap = {} } = instanceReducer

  const getIcon = (status, instance) => {
    const instanceReady = instance?.status === 'running' && instance?.provisioning_status === 'complete'
    if (instanceReady) {
      switch (status) {
        case DEVICE_STATUSES.COMPLETE:
          return <CircleCheckIcon size='sm' className='cm-text-green-400' />
        case DEVICE_STATUSES.FAILED:
          return <ExclamationCircleIcon size='sm' className='cm-text-red-500' />
        case DEVICE_STATUSES.DISSOCIATING:
          return <ProgressCircleIcon size='sm' className='cm-text-orange-600' />
        case DEVICE_STATUSES.ASSOCIATING:
          return <ProgressCircleIcon size='sm' className='cm-text-blue-600' />
        default:
          return null
      }
    }
    if (instance?.provisioning_status === 'error' || instance?.status === 'error') {
      return <ExclamationCircleIcon size='sm' className='cm-text-red-500' />
    }
    if (instance?.provisioning_status === 'initializing' || instance?.provisioning_status === 'provisioning') {
      return <ProgressCircleIcon size='sm' className='cm-text-blue-600' />
    }
    return <ProgressCircleIcon size='sm' className='cm-text-blue-600' />
  }

  const getToolTipLabel = (status, instance) => {
    const instanceReady = instance?.status === 'running' && instance?.provisioning_status === 'complete'
    if (instanceReady) {
      switch (status) {
        case DEVICE_STATUSES.ASSOCIATING:
          return TOOLTIP_ASSOCIATING
        case DEVICE_STATUSES.DISSOCIATING:
          return TOOLTIP_DISSOCIATING
        case DEVICE_STATUSES.FAILED:
          return TOOLTIP_FAILED
        default:
          return null
      }
    }
    return instance?.provisioning_status
  }

  const getToolTipLabelForViewApps = (appName) => {
    return VIEW_APP_TOOLTIP.replaceAll('{appName}', appName)
  }

  const dasApps = useMemo(() => {
    if (!ARRAY.hasArray(value)) {
      return <Body size="lg" addClassName='tw-truncate'>{LABEL_NONE}</Body>
    }
    return value.map(({
      app_id,
      app_id_display_override,
      license_quote_id,
      tsg_id,
      tenant_id,
      region,
      association_status,
      is_view_app_association
    }, index) => {

      const instance = { ...instanceMap[tenant_id], app_id_display_override }
      const instanceReady = instance?.status === 'running' && instance?.provisioning_status === 'complete'

      const notReady = association_status === DEVICE_STATUSES.ASSOCIATING ||
        association_status === DEVICE_STATUSES.FAILED ||
        association_status === DEVICE_STATUSES.DISSOCIATING ||
        !instanceReady
      const appName = getAppName(app_id_display_override || app_id) //getDasAppDisplay(instance, apps, getAppName)?.appName
      return (
        <div key={index} className='tw-truncate tw-flex tw-items-center tw-gap-2'>
          {(notReady) ? (
            <Tooltip label={getToolTipLabel(association_status, instance)}>
              <div className='tw-truncate tw-flex tw-items-center tw-gap-2'>
                {getIcon(association_status, instance)}
                {/* getAppName() */}
                <Body size="lg" addClassName='tw-truncate'>{appName}</Body>
              </div>
            </Tooltip>
          ) :
            (is_view_app_association) ? (
              <Tooltip label={getToolTipLabelForViewApps(appName)}>
                <a className='tw-flex tw-flex-row tw-gap-2 tw-items-center tw-cursor-pointer'
                  onClick={instanceReady ? launchAppInstance.bind(undefined, instance, value) : noop}>
                  {getIcon(association_status, instance)}
                  <Body size="lg" addClassName='tw-text-blue-600 tw-truncate tw-h-full'>{appName}</Body>
                  <OpenNewBrowserIcon size='xxs' className='tw-text-blue-600'/>
                </a>
              </Tooltip>
            ) :
              (
                <a className='tw-flex tw-flex-row tw-gap-2 tw-items-center tw-cursor-pointer'
                  onClick={instanceReady ? launchAppInstance.bind(undefined, instance, value) : noop}>
                  {getIcon(association_status, instance)}
                  <Body size="lg" addClassName='tw-text-blue-600 tw-truncate tw-h-full'>{appName}</Body>
                  <OpenNewBrowserIcon size='xxs' className='tw-text-blue-600'/>
                </a>
              )}
        </div>
      )
    })
  }, [value, instanceMap, getAppName])

  return (
    <div className='tw-flex tw-flex-col tw-truncate'>{dasApps}</div>
  )
}

AppsRenderer.propTypes = {}
export default AppsRenderer
